import { cn } from '@/lib/cn'
import { formatDaysLabel } from '@/lib/nutrition'

export function MacroTile({
  label,
  value,
  unit,
  tone = 'neutral',
}: {
  label: string
  value: string
  unit?: string
  tone?: 'neutral' | 'calories' | 'protein' | 'carbs' | 'fat' | 'fiber'
}) {
  const dot: Record<string, string | undefined> = {
    protein: 'var(--color-protein)',
    carbs: 'var(--color-carbs)',
    fat: 'var(--color-fat)',
    fiber: 'var(--color-fibre)',
  }
  return (
    <div className="min-w-0">
      <p className="flex items-center gap-1.5 truncate text-xs text-ink-faint">
        {dot[tone] ? (
          <span
            aria-hidden
            className="size-1.5 shrink-0 rounded-full"
            style={{ backgroundColor: dot[tone] }}
          />
        ) : null}
        {label}
      </p>
      <p className="tabular mt-1 text-2xl leading-none font-semibold tracking-tight text-ink">
        {value}
        {unit ? <span className="ml-0.5 text-sm font-normal text-ink-faint">{unit}</span> : null}
      </p>
    </div>
  )
}

/**
 * Per-serving macro summary with a proportional split bar.
 *
 * Renders nothing when no ingredient on the recipe carries macro data, which is
 * the case for recipes built only from untracked ingredients.
 */
export function NutritionSummary({
  nutrition,
  compact = false,
}: {
  nutrition: {
    servings: number
    has_data: boolean
    totals: { calories: number; protein: number; carbs: number; fat: number; fiber: number }
    per_serving: { calories: number; protein: number; carbs: number; fat: number; fiber: number }
  }
  compact?: boolean
}) {
  if (!nutrition.has_data) return null
  const split = formatDaysLabel(nutrition.per_serving)

  return (
    <div className="space-y-3">
      <div
        className={cn(
          'grid gap-x-4 gap-y-4',
          compact ? 'grid-cols-3' : 'grid-cols-3 sm:grid-cols-5',
        )}
      >
        <MacroTile label="Calories" value={split.calories} unit="kcal" tone="calories" />
        <MacroTile label="Protein" value={split.protein} unit="g" tone="protein" />
        {!compact ? (
          <MacroTile label="Carbs" value={split.carbs} unit="g" tone="carbs" />
        ) : null}
        {!compact ? (
          <MacroTile label="Fat" value={split.fat} unit="g" tone="fat" />
        ) : null}
        <MacroTile label="Fibre" value={split.fiber} unit="g" tone="fiber" />
      </div>
      <MacroSplitLegend
        proteinPct={split.proteinPct}
        carbsPct={split.carbsPct}
        fatPct={split.fatPct}
      />
    </div>
  )
}

export function MacroSplitLegend({
  proteinPct,
  carbsPct,
  fatPct,
  servings,
}: {
  proteinPct: number
  carbsPct: number
  fatPct: number
  servings?: number
}) {
  if (proteinPct + carbsPct + fatPct <= 0) return null
  const entries = [
    { label: 'Protein', pct: proteinPct, color: 'var(--color-protein)' },
    { label: 'Carbs', pct: carbsPct, color: 'var(--color-carbs)' },
    { label: 'Fat', pct: fatPct, color: 'var(--color-fat)' },
  ]
  return (
    <div>
      <div className="flex h-1.5 w-full gap-0.5 overflow-hidden rounded-full bg-surface-sunken">
        {entries.map((entry) => (
          <span
            key={entry.label}
            title={`${entry.label} ${Math.round(entry.pct)}%`}
            style={{ width: `${entry.pct}%`, backgroundColor: entry.color }}
          />
        ))}
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
        {entries.map((entry) => (
          <span key={entry.label} className="inline-flex items-center gap-1.5 text-xs">
            <span
              aria-hidden
              className="size-2 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-ink-soft">{entry.label}</span>
            <span className="tabular text-ink-faint">{Math.round(entry.pct)}%</span>
          </span>
        ))}
        {servings !== undefined ? (
          <span className="text-xs text-ink-faint">per serving of {servings}</span>
        ) : null}
      </div>
    </div>
  )
}
